'use client'
import { cn } from '@/util/cn'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { type CSSProperties, type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { Icon } from 'ui'
import IconButton from 'ui/src/components/controls/icon-button'

type Option = {
    label: ReactNode
    icon?: string
    /**
     * Include hrefs in the toggle
     * This is useful for SEO
     * Currently this does not render correctly for users, but it works for bots
     */
    href?: string
    gradientClassName?: string
}

type ToggleProps = {
    className?: string
    options: Option[]
    index: number
    onClick?: (index: number) => void
    isMobile?: boolean
    // use this if you need to navigate during the transition
    // if you try to update the url during an animation, it will cancel the animation
    onTransitionEnd?: (index: number) => void
}
const Toggle = ({ className, options, onClick, index, onTransitionEnd, isMobile }: ToggleProps) => {
    const [_index, setIndex] = useState(index)
    const isTransitioning = useRef(false)
    const hasMeasuredMobileToggle = useRef(false)
    const lastCenteredMobileOptionLeft = useRef<number | null>(null)
    const skipNextMobileCenter = useRef(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const optionRefs = useRef<Array<HTMLAnchorElement | HTMLButtonElement | null>>([])
    const pathname = usePathname()
    const isSearchPage = pathname?.startsWith('/search')
    const isEditGifsPage = pathname?.startsWith('/edit/gifs')
    const shouldSizeMobileOptionsToContent = Boolean(isMobile && !isSearchPage)
    const shouldRenderHighlight = !shouldSizeMobileOptionsToContent

    optionRefs.current.length = options.length

    useEffect(() => {
        if (!isTransitioning.current) {
            setIndex(index)
        }
    }, [index])

    useLayoutEffect(() => {
        if (!shouldSizeMobileOptionsToContent) {
            hasMeasuredMobileToggle.current = false
            lastCenteredMobileOptionLeft.current = null
            skipNextMobileCenter.current = false
            return
        }

        const shouldSkipCentering = skipNextMobileCenter.current
        skipNextMobileCenter.current = false

        const container = containerRef.current
        const activeOption = optionRefs.current[_index]

        if (!container || !activeOption) return

        const centeredScrollLeft = () =>
            Math.max(0, activeOption.offsetLeft - (container.clientWidth - activeOption.offsetWidth) / 2)

        const centerActiveOption = () => {
            const left = centeredScrollLeft()

            if (lastCenteredMobileOptionLeft.current === left) return

            container.scrollTo({
                left,
                behavior: hasMeasuredMobileToggle.current ? 'smooth' : 'auto',
            })
            lastCenteredMobileOptionLeft.current = left
            hasMeasuredMobileToggle.current = true
        }

        if (shouldSkipCentering) {
            // A tap already put this option on screen. Leave the strip where the
            // user has it, but remember where centered is so a later resize still recenters.
            lastCenteredMobileOptionLeft.current = centeredScrollLeft()
        } else {
            centerActiveOption()
        }

        const resizeObserver = new ResizeObserver(centerActiveOption)
        resizeObserver.observe(container)
        resizeObserver.observe(activeOption)

        return () => resizeObserver.disconnect()
    }, [_index, shouldSizeMobileOptionsToContent])

    // Equal-width layouts only; content-sized mobile options size themselves.
    const buttonWidth = `${100 / options.length}%`
    const left = `${100 * (_index / options.length)}%`

    const gradient = ['bg-gradient-primary', 'bg-gradient-secondary', 'bg-gradient-tertiary']

    const toggleContent = (
        <div
            key={`toggle-${isMobile ? 'mobile' : 'desktop'}-${isSearchPage ? 'search' : 'normal'}`}
            className={cn(
                'relative flex flex-shrink-0 justify-between overflow-hidden rounded-3xl bg-gradient-grey font-bold text-giphyWhite',
                isMobile
                    ? 'w-max min-w-full'
                    : ['flex w-full justify-between overflow-hidden', !isEditGifsPage && 'md:w-[400px]'],
                !isMobile && className
            )}
        >
            {shouldRenderHighlight && (
                <div
                    data-testid="toggle-highlight"
                    className={cn(
                        'absolute left-0 z-10 h-full transition-all duration-250',
                        isMobile
                            ? 'rounded-3xl bg-gradient-tertiary'
                            : (options[index].gradientClassName || gradient[_index % options.length]) +
                                  ' rounded-[inherit]'
                    )}
                    onTransitionEnd={() => {
                        onTransitionEnd?.(_index)
                        isTransitioning.current = false
                    }}
                    style={{
                        width: buttonWidth,
                        left,
                        transform: isMobile ? 'translateZ(0)' : undefined, // Force hardware acceleration on mobile
                    }}
                />
            )}
            <div
                className={cn(
                    'flex',
                    isMobile ? (isSearchPage ? 'w-full justify-between' : 'gap-2') : 'w-full justify-between'
                )}
            >
                {options.map((option, mapIndex) => {
                    const handleClick = () => {
                        if (_index !== mapIndex) {
                            isTransitioning.current = shouldRenderHighlight
                            skipNextMobileCenter.current = shouldSizeMobileOptionsToContent
                            setIndex(mapIndex)
                            onClick?.(mapIndex)
                            if (!shouldRenderHighlight) {
                                onTransitionEnd?.(mapIndex)
                            }
                        }
                    }

                    const buttonContent = (
                        <>
                            {option.icon && <IconButton inverted icon={option.icon as Icon} />}
                            {option.label}
                        </>
                    )

                    const buttonStyle: CSSProperties = isMobile
                        ? isSearchPage
                            ? {
                                  width: buttonWidth,
                                  minWidth: buttonWidth,
                                  height: '45px',
                                  flexShrink: 0,
                                  whiteSpace: 'nowrap' as const,
                              }
                            : {
                                  minWidth: '88px',
                                  height: '45px',
                                  flexShrink: 0,
                                  scrollSnapAlign: 'center',
                                  whiteSpace: 'nowrap' as const,
                              }
                        : {
                              width: buttonWidth,
                          }

                    return option.href ? (
                        <Link
                            ref={(element) => {
                                optionRefs.current[mapIndex] = element
                            }}
                            prefetch={false}
                            replace
                            onClick={handleClick}
                            className={cn(
                                'relative z-20 flex cursor-pointer items-center justify-center gap-1 rounded-3xl text-center text-inherit',
                                isMobile ? 'px-3 py-2' : 'p-2',
                                shouldSizeMobileOptionsToContent && 'w-max',
                                shouldSizeMobileOptionsToContent && mapIndex === _index && 'bg-gradient-tertiary'
                            )}
                            key={mapIndex}
                            style={buttonStyle}
                            href={option.href}
                        >
                            {buttonContent}
                        </Link>
                    ) : (
                        <button
                            ref={(element) => {
                                optionRefs.current[mapIndex] = element
                            }}
                            onClick={handleClick}
                            className={cn(
                                'relative z-20 flex cursor-pointer items-center justify-center gap-1 rounded-3xl text-inherit',
                                isMobile ? 'px-3 py-2' : 'p-2',
                                shouldSizeMobileOptionsToContent && 'w-max',
                                shouldSizeMobileOptionsToContent && mapIndex === _index && 'bg-gradient-tertiary'
                            )}
                            key={mapIndex}
                            type="button"
                            style={buttonStyle}
                        >
                            {buttonContent}
                        </button>
                    )
                })}
            </div>
        </div>
    )

    if (!isMobile) return toggleContent

    return (
        <div
            ref={containerRef}
            className={cn(
                'scrollbar-hide overflow-x-auto', // eslint-disable-line better-tailwindcss/no-unregistered-classes
                className
            )}
            style={{
                scrollSnapType: 'x mandatory',
                WebkitOverflowScrolling: 'touch',
            }}
        >
            {toggleContent}
        </div>
    )
}
export default Toggle
